import { useAuth } from "context/AuthProvider";
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "services/apiv1";
import { friendlyErrorMessage } from "utils/ErrorMessageUtils";

const useAxiosFunction = () => {
  const [response, setResponse] = useState();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [controller, setController] = useState();
  const { user, replaceUser } = useAuth();
  const userRef = useRef(user);
  const responseInterceptorIdRef = useRef(null);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    if (responseInterceptorIdRef.current !== null) {
      axios.interceptors.response.eject(responseInterceptorIdRef.current);
      responseInterceptorIdRef.current = null;
    }

    const id = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const config = error?.config;
        if (error?.response?.status === 401 && config && !config.sent) {
          config.sent = true;
          try {
            const currentUser = userRef.current;
            const response = await axios.post("/auth/refresh", {
              token: currentUser?.retoken,
              id_user_group: currentUser?.id_user_group,
            });
            const { data } = response.data;
            if (!data?.token) {
              replaceUser(null);
              return Promise.reject(error);
            }

            replaceUser({
              ...currentUser,
              ...data,
            });

            if (data?.token) {
              config.headers = {
                ...config.headers,
                Authorization: `Bearer ${data?.token}`,
              };
            }
            return axios(config);
          } catch (refreshError) {
            replaceUser(null);
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      },
    );

    responseInterceptorIdRef.current = id;
    return () => {
      if (responseInterceptorIdRef.current !== null) {
        axios.interceptors.response.eject(responseInterceptorIdRef.current);
        responseInterceptorIdRef.current = null;
      }
    };
  }, [replaceUser]);

  const axiosFetch = useCallback(
    async (configObj) => {
      const {
        instance = axios,
        method = "GET",
        url,
        auth = false,
        authToken = "",
        multipart = false,
        data = null,
        ...requestConfig
      } = configObj;

      if (data !== null) {
        requestConfig.data = data;
      }

      const currentUser = userRef.current;
      const token = authToken || currentUser?.token;

      if (auth === true && token) {
        requestConfig.headers = {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        };
      }

      if (multipart === true) {
        requestConfig.headers = {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        };
        if (auth === true && token) {
          requestConfig.headers = {
            ...requestConfig.headers,
            Authorization: `Bearer ${token}`,
          };
        }
      }

      try {
        setLoading(true);
        setError("");
        const ctrl = new AbortController();
        setController(ctrl);

        const res = await instance.request({
          url: url,
          method: (method + "").toLowerCase(),
          ...requestConfig,
          signal: ctrl.signal,
        });

        const responseData = res?.data && typeof res.data === "object"
          ? {
              ...res.data,
              message: res.data?.message
                ? friendlyErrorMessage(res.data.message)
                : res.data?.message,
            }
          : res.data;

        setResponse(responseData);
        setError("");
        return responseData;
      } catch (err) {
        const status = parseInt(err?.response?.status, 10);
        if (status === 403) {
          return {
            data: { success: false },
            message: "Mohon maaf, Anda tidak berhak melakukan proses ini!",
          };
        }
        const backendMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.response?.data?.msg ||
          err?.message ||
          "Terjadi kesalahan pada server";
        setError(err.message);
        return {
          data: { success: false },
          message: friendlyErrorMessage(backendMessage),
        };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    return () => controller && controller.abort();
  }, [controller]);

  return [response, error, loading, axiosFetch];
};

export default useAxiosFunction;
